/* eslint-disable no-unused-vars */
import { IAnimListItem, IAnimModel } from '@src/slide/domain/ui-model/element-modeles/Interface/anim-model.interface';
import type {
    IElementBaseModel,
} from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import type {
    IMultiElementsPropertyUpdater,
} from '@src/slide/domain/ui-model/element-modeles/Interface/base/multi-elements-property-updater.interface';
import { IGuideModel as IGuideModelV3 } from '@src/slide/domain/ui-model/element-modeles/Interface/guide-model-v3.interface';
import type {
    ISlideModelProps,
} from '@src/slide/domain/ui-model/element-modeles/Interface/model-props/slide-model-props.interface';
import type { IFillProperty } from '@src/slide/domain/ui-model/element-modeles/property/fill/fill.interface';
import { ITransitionProperty } from '@src/slide/domain/ui-model/element-modeles/property/transition/interface';
import { ITransitionModel } from '@src/slide/domain/ui-model/element-modeles/transition/interface';
import type { ITask } from '@src/slide/infrastructure/module/dependent/taskManager/ITask';
import { ILayoutDataModel, IMasterDataModel, ISlideDataModel } from '@tencent/slide-sdk-model';
import { ICommentGroup } from '@tencent/slide-sdk-model/src/data/comment-data';
import { IObservableArray } from 'mobx';
import { IBatchUpdateFrameProperty } from '../property/frame/batch-update/batch-update.interface';
import { IFooterProps, ISlideFooter } from '../property/slide-footer/slide-footer.interface';
import type { IBaseExtensions, IBaseModel } from './base/base-model.interface';
import type { ILayoutModel } from './layout-model.interface';
import type { IMasterModel } from './master-model.interface';
import type { INoteModel } from './note-model.interface';
import { IThemeModel } from './theme-model.interface';

export type SlideModelSdkTypeV3 = ISlideDataModel | null;

// 批注相关
export type IGroupId = string;
export interface ICommentInfo {
    id: string;
    uin: string;
    nickName: string;
    userImg: string;
    content: string;
    times: number; // 评论时间
}
export type ICommentGroupMap = Map<IGroupId, ICommentGroup>;

export type TBindAnimationParams = {
    uiModel?: IElementBaseModel, // 绑定的元素
    animEffects?: any[],  // 给元素绑定的动画效果
    originId?: string, // 动画效果来源的 ID，当复制的效果的触发器与此 ID 相同时候需要重新将其绑定到新的元素上
    rebind?: boolean, // 是否重新绑定，重绑删除元素之前所有动画
};

export interface ISlideBaseModel extends IBaseModel, IMultiElementsPropertyUpdater {
    props: ISlideModelProps;
    sdkData: ISlideDataModel | IMasterDataModel | ILayoutDataModel | null;
    animList: IObservableArray<IAnimListItem>;
    transition?: ITransitionModel;
    readonly width: number;
    readonly height: number;
    /**
     * 是否有背景
     */
    readonly hasBackground: boolean;
    /**
     *  页面内批量添加元素
     * @param aElementTypes
     * @param aExtraProps
     * @param aExtraInfo
     */
    addElement(aElementTypes: Array<any>|any, aExtraProps: Array<any>|any, aExtraInfo?): void;

    /**
     * 插入一个元素，不要选中他，上面那个方法会选中
     * @param aElementTypes
     * @param aExtraProps
     * @param aExtraInfo
     */
    addElementWithNoSelect(aElementTypes: Array<any>|any, aExtraProps: Array<any>|any, aExtraInfo?): void;
    /**
     * 根据ParagraphList添加一个Shape元素 TODO：这个接口很奇怪，应该把addElement拓展来包含这里的能力
     * @param extraProps
     * @param paragraphList
     */
    addElementWithParagraphList(extraProps, paragraphList): void;
    /**
     * 页面内批量删除元素
     * @param elements
     */
    deleteElement(elements?: Array<any>): void;
    /**
     * 批量调整元素顺序/层级
     * @param toIndex
     * @param elements
     * @param mergeMove
     */
    moveElement(toIndex: number, elements?: Array<any>, mergeMove?: boolean): void;
    /**
     * 使得元素置顶或置底
     * @param moveType
     * @param elements
     */
    setCurrentElementToFrontOrBack(moveType: string, elements?: Array<any>): void;
    /**
     * 删除占位符的同时添加上新的元素
     * @param delEles
     * @param addNewEleInfos
     */
    deletePlaceHolderAndAddNewElements(delEles: Array<any>, addNewEleInfos: Array<any>): void;
    /**
     * 将smartArt转化成组合
     * @param elements
     */
    convertToGroup(elements?: Array<any>): void;
    /**
     * 使元素成组
     * @param elements
     */
    groupElements(elements?: Array<any>): void;
    /**
     * 解组元素
     * @param elements
     */
    unGroupElements(elements?: Array<any>): void;
    /**
     * 批量为元素添加动画
     * @param uiModels
     * @param animType
     * @param subType
     */
    addAnimation(uiModels: Array<any>, animType: string, subType?: string): boolean;
    /**
     * 修改元素动画数据
     * @param animModel
     * @param animModiyParam
     */
    modifyAnimation(animModel: any, animModiyParam: any): void;

    /**
     * 绑定动画
     * 批量为单个元素添加多组动画，并预设一些属性
     * @param params TBindAnimationParams
     * @returns boolean 是否绑定成功
     */
    bindAnimation(params: TBindAnimationParams): boolean;
    /**
     * 调整页面内动画顺序
     * @param fromIndex
     * @param toIndex
     * @param fromClickSpid
     * @param toClickSpid
     */
    moveAnimation(fromIndex: number, toIndex: number, fromClickSpid: string, toClickSpid: string): void;
    /**
     * 删除动画
     * @param animModel
     */
    removeAnimation(animModel: any): void;
    /**
     * 通过sdk的id查找页面内的元素，返回UIModel或null
     * @param childId
     */
    childUiModelFromId(childId: string): any;
    /**
     * 获取页面内所指定类型的占位符元素
     * @param type
     */
    getElementsOfPlaceholderTypeInSlideExceptSlected(type: string): Array<any>;
    /**
     * 全选页面内元素
     */
    selectAll(): void;

    // ref 方法重构至此
    willUpdateElement(ele: IElementBaseModel): void;
    updateModifiedElements(): void;
    getStyleValue(ele, props, eleModel, attribute);
    updateOperationFrame(ele: IElementBaseModel): void;
    getAnimationClickGroup(animListItem: IAnimListItem): IAnimModel[][]
}

export interface ISlideModel extends ISlideBaseModel {
    children: Array<IElementBaseModel>;
    preload: boolean;
    timing: any;
    timingModel?: any;
    guideModel?: IGuideModelV3;
    displayChildrenElement: boolean;
    pptEditorState: number;
    childrenConverted?: boolean;
    animConverted?: boolean;
    mainSlideTimeline?: any;
    noteData: INoteModel;
    slideIndex: number | null;
    layoutId?: string;
    /**
     * 为 false 时代表隐藏页面。默认为 true
     */
    isShowInPlay: boolean;
    slideFooter: ISlideFooter;
    showMasterSp: number;
    readonly elementNumInfo: any;
    readonly convertTaskGroup: ITask;
    readonly isSlide?: boolean;
    readonly isLayout?: boolean;
    readonly isMaster?: boolean;
    readonly childrenPlaceholderInfo: any;
    listIndex?: number;
    name: string;

    highlightModelId: string | null;
    /**
     * 当前slide的所有批注
     */
    commentGroupMap?: ICommentGroupMap;
    commentGroupInfoMap?: Map<IGroupId, ICommentInfo[]>;
    extensions: ISlideExtensions;
    cancelConvertChildrenTask(): void;
    convertAnimDataSdkToUi(): void;
    getMainSequence(): any;
    includeInheritChildren(): Array<any>;
    startConvertChildrenTask(priority?: any): Promise<void>;
    convertTimingDataSdkToUi(): Promise<void>;
    /**
     * Fill相关接口
     */
    getBackgroundFill(): IFillProperty;

    getRelationLayoutModel(): any & ILayoutModel;
    getRelationMasterModel(): any & IMasterModel;
    getRelationThemeModel(): any & IThemeModel;
    changeLayout(masterIndex, layoutIndex);
    changeLayoutLite(masterDataModel, layoutDataModel);
    findChildByPlTypeNum(plTypeNum);
    toggleShowSlide(show: boolean): void;
    syncConvertRelationModelChildren(): any;
    getMasterId(): string;
    // 获取所有子元素里的占位符
    getAllChildrenOfPlaceHolder(): IElementBaseModel[];
    updatePlaceholderFormat(props: IFooterProps);
    /**
     * 批量更新frame
     */
    getBatchUpdateFrame(): IBatchUpdateFrameProperty;
    getTransitionProperty(): ITransitionProperty;
    isSlideSupportAI(): boolean;
    getAIType(): number | string | undefined;
}

export const MoveType = {
    FRONT: 'front',
    BACK: 'back',
};

/**
 * 页上的扩展字段，目前没有所以是空
 */
export interface ISlideExtensions extends IBaseExtensions {}
